import { createTheme, drawerClasses, PaletteMode } from '@mui/material'
// import below is required to customize the DataGrid theme
// see https://mui.com/x/react-data-grid/getting-started/#typescript
// oxlint-disable-next-line no-empty-named-blocks require-module-specifiers
import type {} from '@mui/x-data-grid-premium/themeAugmentation'

declare module '@mui/material/styles' {
	interface Palette {
		ai: {
			main: string
			contrastText: string
		}
	}
	interface PaletteOptions {
		ai?: {
			main: string
			contrastText: string
		}
	}
}

export default function GetTheme(mode: PaletteMode) {
	const palette = {
		mode,
		primary: {
			main: 'rgb(68, 197, 113)',
			contrastText: '#FFF',
		},
		secondary: {
			main: 'rgb(216, 158, 71)',
			contrastText: '#FFF',
		},
		info: {
			main: 'rgba(94, 128, 216, 0.2)',
			contrastText: '#FFF',
		},
		numberUnit: {
			main: '#F00',
			contrastText: '#979797',
		},
		ai: {
			main: '#A181FA',
			contrastText: '#FFF',
		},
		emissionTypes: {
			waste: '#D89E47',
			water: '#58C0C0',
			co2: '#44C571',
			spend: '#BF78CB',
			volume: '#296D62',
		},
		backgroundColors: {
			core: '#44C571',
			grey: '#2B2D2B',
			darkGrey: '#2F2F2F',
			opaque: 'rgb(19, 14, 18)',
			dark: 'rgba(0, 0, 0, 0.8)',
			darkBlue: '#222A3A',
			opaqueTransparent: 'rgba(19, 14, 18, 0.5)',
			black: '#000',
			blue: '#2d56e7',
			errorLight: '#2C0707',
			greenLight: '#0C2715',
			lighterGrey: '#ffffff14',
			warningLight: '#331500',
			info: '#00B3FF',
			cdp: '#d31144',
		},
		borderColors: {
			gray: ' #B0B0B0',
			darkGray: '#585D59',
			darkestGray: '#2B2D2B',
			greenishgrey: '#697a6d',
			darkBlur: '#404441',
			lightGrey: '#393939',
			transparentWhite: 'rgba(255, 255, 255, 0.2)',
		},
		elementColors: {
			bad: '#FE9696',
			good: '#72E58B',
			warningLight: '#C0EA6F',
			warning: '#FFEB59',
			warningHeavy: '#FFCD6F',
		},
		modalColors: {
			headerBackgroundColor: '#3a3a3a',
			backgroundColor: '#282828',
			borderColor: '#525252',
			stepColor: '#EDEDED',
			stepIconColor: '#D9D9D9',
			stepBorderColor: '#525252',
		},
		icons: {
			primary: '#F4F4F4',
			secondary: '#CDCDCD',
		},
		fonts: {
			primary: '#F4F4F4',
			secondary: '#CDCDCD',
			tertiary: '#8D8D8D',
			white: '#FFF',
			darkGrey: '#6F6F6F',
			lightGrey: '#CECECE',
			green: '#44C571',
			warning: '#ffa726',
			black: '#0D0D0D',
			error: '#E34F4F',
			disabled: '#646865',
		},
		statuses: {
			inProgress: 'black',
			notStarted: '#393939',
			requested: '#5752BA',
			pendingReview: '#3E5BC1',
			completed: '#319457',
			rejected: '#92382C',
			declined: '#FF3838',
			unknown: 'grey',
		},
		stackBarColors: {
			grey1: '#DFDFDF',
			grey2: '#D1D1D1',
			grey3: '#ABABAB',
			grey4: '#8E8E8E',
			grey5: '#686868',
		},
		tableColors: {
			header: '#393939',
			row: '#262626',
		},
	}
	return createTheme({
		spaces: {
			sm: '0.25rem',
			m: '0.625rem',
			l: '1rem',
			xl: '1.5rem',
		},
		palette,
		typography: {
			fontFamily: "'Roboto', sans-serif",
			h1: {
				fontSize: '2rem',
				fontWeight: 400,
				letterSpacing: 0,
				lineHeight: '3rem',
			},
			h2: {
				fontSize: '1.25rem',
				fontWeight: 400,
				lineHeight: '2rem',
			},
			h3: {
				fontSize: '1rem',
				fontWeight: 400,
				lineHeight: '2rem',
			},
			h4: {
				fontWeight: 400,
				fontSize: '2rem',
				lineHeight: '2.5rem',
				letterSpacing: 0.25,
			},
			h5: {
				fontWeight: 400,
				fontSize: '1.5rem',
				lineHeight: '2rem',
			},
			h6: {
				fontWeight: 600,
				fontSize: '1.25rem',
				lineHeight: '2rem',
			},
			subtitle1: {
				fontWeight: 600,
				size: '1.125rem',
				lineHeight: '1.25rem',
				letterSpacing: 0.15,
			},
			body1: {
				fontWeight: 400,
				size: '1rem',
				lineHeight: '1.25rem',
				letterSpacing: 0.15,
			},
			body2: {
				fontWeight: 500,
				size: '0.875rem',
				lineHeight: '1.25rem',
				letterSpacing: 0.17,
			},
			description: {
				color: '#979B98',
				fontWeight: 400,
				fontSize: '0.875rem',
			},
			button: {
				textTransform: 'none',
				fontWeight: 'bold',
				color: 'white',
				size: '0,875rem',
				letterSpacing: 0.46,
				lineHeight: '1.625rem',
			},
		},
		components: {
			MuiDataGrid: {
				styleOverrides: {
					root: {
						border: 'none',
						'& .MuiDataGrid-row': {
							backgroundColor: palette.tableColors.row,
						},
						'& .MuiDataGrid-column': {},
						'& .MuiDataGrid-cell': {
							borderBottom: `1px solid ${palette.backgroundColors.opaque}`,
							borderRight: `1px solid ${palette.backgroundColors.opaque}`,
						},

						'& .MuiDataGrid-columnHeader': {
							backgroundColor: palette.tableColors.header,
							borderBottom: `1px solid ${palette.backgroundColors.opaque}`,
							borderRight: `1px solid ${palette.backgroundColors.opaque}`,
							padding: '1rem',
						},
						'& .MuiDataGrid-footerContainer': {
							backgroundColor: palette.tableColors.header,
							borderRadius: '0 0 0.25rem',
						},
						'& .MuiDataGrid-columnHeaderTitle': {
							fontWeight: 600,
						},
						'& .MuiDataGrid-withBorderColor': {
							borderColor: palette.backgroundColors.opaque,
						},
					},
				},
			},

			MuiTabs: {
				styleOverrides: {
					flexContainer: {
						display: 'inline-block',
					},
				},
			},
			MuiModal: {
				styleOverrides: {
					root: {
						'.MuiBackdrop-root': {
							backgroundColor: 'rgba(0, 0, 0, 0.97)',
						},
					},
				},
			},
			MuiPopover: {
				styleOverrides: {
					root: {
						'.MuiBackdrop-root': {
							backgroundColor: 'transparent',
						},
					},
					paper: {
						maxWidth: '50%',
					},
				},
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						background: '#2c2c2c',
						color: '#c6c6c6',
						fontSize: '0.75rem',
					},
				},
			},

			MuiDrawer: {
				styleOverrides: {
					root: {
						zIndex: 1300,
						position: 'absolute',
						[`.${drawerClasses.paper}`]: {
							backdropFilter: 'blur(3px)',
							backgroundColor: 'rgb(0, 0, 0, 0.8)',
						},
					},
				},
			},
			MuiInput: {
				styleOverrides: {
					underline: {
						'&:before, &:after': {
							borderBottom: '1px solid #585D59',
						},
						'&:hover:before , &:hover:after': {
							borderBottom: '1px solid #585D59',
						},
					},
				},
			},
			MuiTextField: {
				defaultProps: {
					variant: 'standard',
				},
				styleOverrides: {
					root: {
						'& input[type=number]': {
							MozAppearance: 'textfield',
						},
						'& input[type=number]::-webkit-outer-spin-button': {
							WebkitAppearance: 'none',
							margin: 0,
						},
						'& input[type=number]::-webkit-inner-spin-button': {
							WebkitAppearance: 'none',
							margin: 0,
						},
					},
				},
			},
			MuiSelect: {
				styleOverrides: {
					select: {
						underline: {
							'&:before, &:after': {
								borderBottom: '1px solid #585D59',
							},
							'&:hover:before , &:hover:after': {
								borderBottom: '1px solid #585D59',
							},
						},
					},
					icon: {
						height: '100%',
						top: '50%',
						transform: 'translateY(-50%)',
						width: '1.2em',
						borderRadius: '0px 4px 0px 0px',
					},
				},
			},
			MuiTablePagination: {
				styleOverrides: {
					root: {
						background: 'rgb(57, 57, 57)',
						borderRadius: '0 0 0.25rem',
					},
					selectIcon: {
						top: 'calc(40% - 0.5em)',
						right: '-2px',
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					paper: {
						overflowY: 'hidden',
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: {
						backgroundColor: 'transparent',
						alignItems: 'flex-start',
						minWidth: '200px',
					},
				},
			},
			MuiCard: {
				styleOverrides: {
					root: {
						borderRadius: 6,
					},
				},
			},
			MuiChip: {
				styleOverrides: {
					root: {
						margin: '5px 5px 5px 0',
					},
				},
			},
			MuiBadge: {
				styleOverrides: {
					anchorOriginTopRightRectangular: {
						background: '#616161',
						color: '#fff',
					},
				},
			},
			MuiFormControl: {
				defaultProps: {
					variant: 'standard',
				},
				styleOverrides: {
					root: {
						padding: '4px',
					},
				},
			},
			MuiAccordion: {
				styleOverrides: {
					root: {
						background: 'none',
					},
				},
			},
			MuiAccordionSummary: {
				styleOverrides: {
					root: {
						background: 'none',
						borderBottom: 'none',
						boxShadow: 'none',
					},
				},
			},
			MuiLink: {
				styleOverrides: {
					root: {
						color: '#44C571',
						cursor: 'pointer',
						fontSize: '0.875rem',
						fontWeight: 500,
						lineHeight: '2rem',
						textDecoration: 'underline',
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					root: {
						color: '#000',
						whiteSpace: 'nowrap',
					},
					contained: {
						background: 'linear-gradient(180deg, #44C571 0%, #1C9747 100%)',
						':disabled': {
							background: '#FFFFFF0F',
						},
					},
					outlined: {
						border: '2px solid #ffffff33',
						borderRadius: 4,
						color: '#44C571',
						':hover': {
							border: '2px solid',
						},
					},
					text: {
						color: '#44C571',
						cursor: 'pointer',
						fontSize: '0.875rem',
						fontWeight: 500,
						lineHeight: '2rem',
						textDecoration: 'underline',
					},
				},
			},
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						fontSize: '0.875rem',
					},
					a: {
						textDecoration: 'none',
						color: '#44C571',
					},
				},
			},
		},
	})
}
